import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import createIcon from '../../../asset/icon/create.png';
import editIcon from '../../../asset/icon/edit.png';
import HintSmallWrapper from '../../../shared/component/headless/button/HintSmallWrapper';
import { getHintMessage } from '../../../shared/constants/hintMessages';
import { VenueTagData } from '../../../shared/dto/EntityRepository';
import { ModalType } from '../../../shared/enum/EnumRepository';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { useAdminVenueTag } from '../../share/hook/useAdminVenueTag';
import { useAdminVenueStore } from '../../share/store/adminVenueStore';
import { runAfterVenueListContextChange } from './runAfterUnsavedVenueCheck';

const VenueTagStation: React.FC = () => {
  const { setStandardModal } = useModalStore();
  const { tags, getVenueTagList } = useAdminVenueTag();
  const filterData = useAdminVenueStore((state) => state.filterData);
  const setFilterData = useAdminVenueStore((state) => state.setFilterData);
  const didRequestTags = useRef(false);

  useEffect(() => {
    if (didRequestTags.current) return;
    didRequestTags.current = true;
    void getVenueTagList();
    // getVenueTagList는 hook 렌더마다 새 참조이므로 mount 시 한 번만 조회한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openTagStation = () => {
    setStandardModal({
      modalType: ModalType.VENUE_TAG_STATION,
      title: 'Labels',
      value: '',
      handleChange: () => {},
    });
  };

  const openCreateTag = () => {
    setStandardModal({
      modalType: ModalType.CREATE_VENUE_TAG,
      title: 'Create Tag',
      value: '',
      handleChange: () => {},
    });
  };

  const toggleTag = (tag: VenueTagData) => {
    const isSelected = filterData.tagIds.includes(tag.id);
    runAfterVenueListContextChange(() => {
      setFilterData({
        ...filterData,
        tagIds: isSelected
          ? filterData.tagIds.filter((tagId) => tagId !== tag.id)
          : [...filterData.tagIds, tag.id],
      });
    });
  };

  return (
    <VenueTagStationComp>
      <BtnContainer>
        <HintSmallWrapper content={getHintMessage('CHANGE_TAGS')}>
          <UniversalIconBtn
            size="fit"
            border={false}
            isTransparent
            backgroundImg={editIcon}
            onClick={openTagStation}
          />
        </HintSmallWrapper>
        <HintSmallWrapper content={getHintMessage('ADD_TAG')}>
          <UniversalIconBtn
            size="small"
            border={false}
            isTransparent
            backgroundImg={createIcon}
            onClick={openCreateTag}
          />
        </HintSmallWrapper>
      </BtnContainer>
      {tags.length > 0 && (
        <TagContainer>
          {tags.map((tag) => {
            const isSelected = filterData.tagIds.includes(tag.id);
            return (
              <TagFactor
                key={tag.id}
                title={tag.name}
                $isSelected={isSelected}
                onClick={() => toggleTag(tag)}
              >
                {tag.name}
              </TagFactor>
            );
          })}
        </TagContainer>
      )}
    </VenueTagStationComp>
  );
};

const VenueTagStationComp = styled.section`
  width: fit-content;
  min-width: 0;
  height: calc(2vh);
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
`;

const TagContainer = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  overflow-x: auto;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const TagFactor = styled.span<{ $isSelected: boolean }>`
  width: fit-content;
  flex-shrink: 0;
  border-bottom: 1px dotted ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.color_Gray_03 : 'transparent'};
  color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.color_Gray_03 : theme.colors.color_Gray_05};
  opacity: ${({ $isSelected }) => ($isSelected ? 1 : 0.4)};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
`;

const BtnContainer = styled.div`
  width: fit-content;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export default VenueTagStation;
